/**
 * Triage slice.
 *
 * The Triage panel backlog, and conversion between triage items and tree nodes.
 *
 * Slices share one store: `set` and `get` are typed over the whole AppStore,
 * so an action here can read or write another slice's state where the domains
 * genuinely interact (loading a project resets the tree, converting a triage
 * item calls the tree's addNode).
 */
import type { StateCreator } from 'zustand';
import type { NewTriageItemPayload, TriageItem, TriageItemDragState } from '../../types';
import { childTypeOf, flattenTreeNodes, randomId, removeNodeFromTree } from '../../utils/ostUtils';
import { initialData } from '../initialData';
import { patchData } from '../persistence';
import type { AppStore } from '../storeTypes';

export const createTriageSlice: StateCreator<
    AppStore,
    [],
    [],
    Pick<
        AppStore,
        | 'triageItems'
        | 'triagePanelMinimized'
        | 'nodeDragOverTriage'
        | 'triageItemDrag'
        | 'addTriageItem'
        | 'updateTriageItem'
        | 'deleteTriageItem'
        | 'reorderTriageItems'
        | 'setTriagePanelMinimized'
        | 'setNodeDragOverTriage'
        | 'beginTriageItemDrag'
        | 'cancelTriageItemDrag'
        | 'convertNodeToTriageItem'
        | 'convertTriageItemToNode'
        | 'prepareTriageItemForPlacement'
    >
> = (set, get) => ({
    // ─── Triage Panel initial state ──────────────────────────────────────────
    triageItems: initialData.triageItems || [],

    triagePanelMinimized: initialData.triagePanelMinimized ?? true,
    nodeDragOverTriage: false,
    triageItemDrag: null,


    // ─── Triage Panel actions ────────────────────────────────────────────────

    addTriageItem: (data: NewTriageItemPayload) => {
        set((state) => {
            const currentItems = state.triageItems ?? [];
            const id = randomId();
            const newItem: TriageItem = {
                id,
                type: 'triage',
                title: data.title || 'Untitled',
                description: data.description || '',
                order: currentItems.length,
                sourceNodeType: data.sourceNodeType,
            };
            const nextItems = [...currentItems, newItem];
            patchData({ triageItems: nextItems });
            return { triageItems: nextItems };
        });
    },


    updateTriageItem: (id: string, data: Partial<Omit<TriageItem, 'id' | 'type'>>) => {
        set((state) => {
            const nextItems = (state.triageItems ?? []).map((item) =>
                item.id === id ? { ...item, ...data } : item,
            );
            patchData({ triageItems: nextItems });
            return { triageItems: nextItems };
        });
    },


    deleteTriageItem: (id: string) => {
        set((state) => {
            const filtered = (state.triageItems ?? []).filter((item) => item.id !== id);
            // Re-assign order values to keep them contiguous after deletion
            const nextItems = filtered.map((item, index) => ({ ...item, order: index }));
            patchData({ triageItems: nextItems });
            return { triageItems: nextItems };
        });
    },


    reorderTriageItems: (fromIndex: number, toIndex: number) => {
        set((state) => {
            const items = state.triageItems ?? [];
            if (
                fromIndex === toIndex ||
                fromIndex < 0 || fromIndex >= items.length ||
                toIndex < 0 || toIndex >= items.length
            ) {
                return state;
            }
            const next = [...items];
            const [removed] = next.splice(fromIndex, 1);
            next.splice(toIndex, 0, removed);
            const nextItems = next.map((item, index) => ({ ...item, order: index }));
            patchData({ triageItems: nextItems });
            return { triageItems: nextItems };
        });
    },


    setTriagePanelMinimized: (minimized: boolean) => {
        set({ triagePanelMinimized: minimized });
        patchData({ triagePanelMinimized: minimized });
    },


    setNodeDragOverTriage: (over: boolean) => {
        set({ nodeDragOverTriage: over });
    },


    beginTriageItemDrag: (drag: TriageItemDragState) => {
        set({ triageItemDrag: drag });
    },


    cancelTriageItemDrag: () => {
        set({ triageItemDrag: null });
    },


    convertNodeToTriageItem: (nodeId: string): boolean => {
        const state = get();

        // Find the node in any collection
        const allTreeNodes = flattenTreeNodes(state);
        const node = allTreeNodes.find((n) => n.id === nodeId);
        if (!node) return false;

        // Only leaf nodes (no children) can be converted to avoid orphaning subtrees
        if (!get().canDeleteNode(node.type, nodeId)) return false;

        const currentItems = state.triageItems ?? [];
        const newItem: TriageItem = {
            id: randomId(),
            type: 'triage',
            title: node.title,
            description: node.description || '',
            order: currentItems.length,
            sourceNodeType: node.type,
        };

        set((s) => {
            const nextState = { ...s, ...removeNodeFromTree(s, node.type, nodeId) };

            // Remove its canvas position
            const nextPositions = { ...s.positions };
            delete nextPositions[nodeId];
            nextState.positions = nextPositions;

            const nextItems = [...(s.triageItems ?? []), newItem];
            nextState.triageItems = nextItems;

            // When converting a node to a triage item we are effectively removing it
            // from the tree. Strip any insight link targets that referenced this node
            // so highlights are removed from insight cards.
            const nextInsightLinks = (s.insightLinks || [])
                .map((l) => ({ ...l, targetNodeIds: l.targetNodeIds.filter((id) => id !== nodeId) }))
                .filter((l) => l.targetNodeIds.length > 0);

            // Update in-memory nextState so UI reflects the removal immediately
            nextState.insightLinks = nextInsightLinks;

            patchData({
                outcome: nextState.outcome,
                opportunities: nextState.opportunities,
                solutions: nextState.solutions,
                userStories: nextState.userStories,
                positions: nextPositions,
                triageItems: nextItems,
                insightLinks: nextInsightLinks,
            });

            return nextState;
        });

        return true;
    },


    convertTriageItemToNode: (itemId: string, targetNodeId: string) => {
        const state = get();
        const item = (state.triageItems ?? []).find((t) => t.id === itemId);
        if (!item) return;

        // Find the target tree node to determine the child type
        const allTreeNodes = flattenTreeNodes(state);
        const targetNode = allTreeNodes.find((n) => n.id === targetNodeId);
        if (!targetNode) return;

        // Determine the appropriate child type based on the target's type
        const childType = childTypeOf(targetNode.type, state.solutionChildType || 'story');
        // Story and experiment nodes cannot have children
        if (!childType) return;

        // Create the new child node from the triage item's data
        get().addNode(childType, { title: item.title, description: item.description }, targetNodeId);

        // Remove the triage item now that it is placed in the tree
        set((s) => {
            const filtered = (s.triageItems ?? []).filter((t) => t.id !== itemId);
            const nextItems = filtered.map((t, i) => ({ ...t, order: i }));
            patchData({ triageItems: nextItems });
            return { triageItems: nextItems };
        });
    },


    /**
     * Removes the triage item from the panel and returns the data needed to
     * start the pending-node placement flow in Canvas.tsx.  The caller is
     * responsible for calling onStartPendingNodes with the returned payload.
     */
    prepareTriageItemForPlacement: (itemId: string, targetNodeId: string) => {
        const state = get();
        const item = (state.triageItems ?? []).find((t) => t.id === itemId);
        if (!item) return null;

        const allTreeNodes = flattenTreeNodes(state);
        const targetNode = allTreeNodes.find((n) => n.id === targetNodeId);
        if (!targetNode) return null;

        const childType = childTypeOf(targetNode.type, state.solutionChildType || 'story');
        if (!childType) return null;

        // Remove the triage item — the caller will create the node via pendingNodes flow
        set((s) => {
            const filtered = (s.triageItems ?? []).filter((t) => t.id !== itemId);
            const nextItems = filtered.map((t, i) => ({ ...t, order: i }));
            patchData({ triageItems: nextItems });
            return { triageItems: nextItems };
        });

        return { type: childType, parentId: targetNodeId, title: item.title, description: item.description };
    },
});
